/**
 * A step-by-step wizard: one step on screen, the rest listed beside it.
 *
 * **Both creation flows were numbered lists on one long page.** Numbering the
 * sections said what the order *was*; it did not make the order happen, so
 * the page still showed a form whose last field was the one that actually
 * blocked you.
 *
 * ⚠ **The objection this shell has to answer** is written into the character
 * page's own docstring, and it is a real one: *"a wizard that hides the
 * constraints behind Next buttons is one that refuses you at the end without
 * saying which step was wrong."* Every choice in character creation constrains
 * the others — a race you qualify for depends on what you rolled, a class on
 * what the race made of it, an alignment on the class — so three rules hold
 * this up:
 *
 *  1. **A step names what it is waiting for**, on the Next button and in the
 *     list, so a blocked step is never a dead control with no cause. Nothing
 *     here decides anything: the reason is the server's rule read back.
 *  2. **Every step you have finished stays reachable.** Going back is one
 *     click and loses nothing — the choice is still there once you land on
 *     the step, it is just not previewed in the list above it.
 *  3. **The last step is a review** that shows the whole character before it is
 *     made. A wizard whose final press is a surprise is the failure mode above.
 *
 * The shell is deliberately dumb — it renders steps and moves between them. Which
 * steps exist, what blocks them and what each one says are the caller's, and in
 * both callers they are computed by a **pure function** with its own tests: the
 * bug and the fix in that logic produce identical markup, which is the trap
 * `terrainVisible` and `selectedActionOf` were both caught by.
 *
 * ⚠ **The step list is navigation, and only navigation.** It used to carry
 * a summary of each step's answer too, on the reasoning that the constraint
 * should stay visible while you work on the next one — but a table's whole
 * width of secondary text competing with the numbers and titles is what
 * actually made the sequence hard to read, which is the opposite of what
 * the summaries were for. Reversed: nothing renders here but a step's
 * number, its title, and whether it is done.
 */

import type { ReactNode } from 'react';

export interface WizardStep {
  /** Stable across renders; used as the key and for tests. */
  readonly key: string;
  readonly title: string;
  /** Whether this step has been satisfied. */
  readonly done: boolean;
  /**
   * Why it has not been, in the player's words.
   *
   * Named rather than merely greyed: a disabled button with no reason on it is
   * indistinguishable from a broken one.
   */
  readonly blockedBy?: string;
  /** A step that may be passed with nothing done. */
  readonly optional?: boolean;
  /**
   * This step's own control moves you on — do not also draw a generic Next.
   *
   * ⚠ **A step whose own control ends the wizard cannot also use `finish`.**
   * A step like the campaign wizard's old `review` — its own action button
   * (matching the pattern `world`'s Generate and `map`'s Set already use)
   * sat next to a generic Next that could never usefully be pressed: the
   * button's own success handler navigates away the instant it succeeds,
   * before Next could ever go from disabled to clicked.
   * A control that can never be the one you press is worse than no control —
   * it asks "is there something else I need to do?" — so this step type
   * drops Next (and `blockedBy`, which only ever explained why Next was
   * stuck) entirely, leaving Back and the step's own action as the only two
   * things on screen.
   */
  readonly selfAdvances?: boolean;
}

/** Whether you may move on from this step. */
export function canLeave(step: WizardStep | undefined): boolean {
  if (!step) return false;
  return step.done || step.optional === true;
}

/**
 * The furthest step you may jump to.
 *
 * Everything up to and including the first unfinished step: going back is
 * always allowed, and skipping ahead past something unresolved is what would
 * produce the refusal-at-the-end this shell exists to avoid.
 */
export function furthestReachable(steps: readonly WizardStep[]): number {
  const stuck = steps.findIndex((step) => !canLeave(step));
  return stuck === -1 ? steps.length - 1 : stuck;
}

export function Wizard({
  steps,
  current,
  onGo,
  children,
  finish,
}: {
  readonly steps: readonly WizardStep[];
  readonly current: number;
  readonly onGo: (index: number) => void;
  /** The current step's own controls. */
  readonly children: ReactNode;
  /** What the last step's forward button does and says, when it is not Next. */
  readonly finish?: { readonly label: string; readonly onPress: () => void; readonly busy?: boolean };
}) {
  const step = steps[current];
  const reachable = furthestReachable(steps);
  const isLast = current === steps.length - 1;

  return (
    <div className="space-y-5">
      {/* ⚠ **Static wayfinding, nothing else.** It used to swap each step's
          number for a checkmark and strike its title
          through the instant `done` went true — which, for the steps with a
          text box in them, is every keystroke: the Name step's icon flipped
          between "5" and "✓" as you typed and deleted a single letter. All
          that is left is a step's number, its title, and whether it is the
          one you are standing on — none of which changes while you type,
          only when you actually move. */}
      <ol className="flex flex-wrap items-center gap-y-1 text-xs">
        {steps.map((one, index) => (
          <li key={one.key} className="flex items-center">
            {/* ⚠ **Said to be a sequence, not just shown as one.** `gap-x-4`
                alone reads as an unordered list of labels — a real arrow
                between steps is what a screen only sees when it is told, and
                the same reason `blockedBy` names a rule instead of just
                greying a button. `aria-hidden` because the order is already
                in the `<ol>` and in each step's own number. */}
            {index > 0 && (
              // ⚠ **Sized up on its own.** Inheriting the list's `text-xs`
              // drew a 12px arrow glyph, which renders as a hairline at that
              // size regardless of colour — a decoration nobody could
              // actually see is the same failure as one nobody could read.
              <span
                aria-hidden="true"
                className="mx-2 text-base font-bold text-neutral-300 dark:text-neutral-600"
              >
                →
              </span>
            )}
            <button
              type="button"
              // Backwards always, forwards only as far as the first
              // unfinished step: a jump past something unresolved is exactly
              // the refusal-at-the-end this shell exists to prevent. This is
              // the one place `done` still acts — it decides what you may
              // click, never what a step visibly looks like.
              //
              // ⚠ **No `disabled:opacity` here on purpose.** A step you
              // cannot reach yet is still one you need to be able to READ —
              // it is the preview of what is coming, not a control you are
              // being told off for touching. Stacked on top of the already-
              // muted colour below, an opacity multiplier made a whole list
              // of upcoming steps close to unreadable against a dark
              // background; `disabled` alone already stops the click and
              // shows a not-allowed cursor.
              disabled={index > reachable}
              aria-current={index === current ? 'step' : undefined}
              onClick={() => onGo(index)}
              className={`text-left ${
                index === current
                  ? 'font-semibold text-neutral-900 dark:text-neutral-100'
                  : 'text-neutral-600 dark:text-neutral-300'
              }`}
            >
              {/* ⚠ **The number is `aria-hidden`, or it joins the accessible
                  name.** Without it, a step's name is "1 Race and class" —
                  every caller (including `e2e`'s own `getByRole('button',
                  { name: … })`) that wants "the button named after this
                  step" now has to know its position too. The order is
                  already in the `<ol>` itself; hiding the digit here does
                  not remove it from the page, only from the name. */}
              <span aria-hidden="true">{index + 1}</span> {one.title}
            </button>
          </li>
        ))}
      </ol>

      <section className="space-y-3" aria-label={step?.title ?? 'Step'}>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-600 dark:text-neutral-300">
          {current + 1} · {step?.title}
          {step?.optional && <span className="ml-1 normal-case font-normal">(optional)</span>}
        </h2>
        {children}
      </section>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          disabled={current === 0}
          onClick={() => onGo(current - 1)}
          className="rounded-md border border-neutral-300 px-3 py-1 text-sm disabled:opacity-40 dark:border-neutral-700"
        >
          Back
        </button>
        {isLast && finish ? (
          <button
            type="button"
            disabled={!canLeave(step) || finish.busy === true}
            onClick={finish.onPress}
            className="rounded-md border border-emerald-600 bg-emerald-50 px-4 py-2 text-sm font-medium disabled:border-neutral-300 disabled:bg-transparent disabled:opacity-40 dark:bg-emerald-950 dark:disabled:border-neutral-700"
          >
            {finish.label}
          </button>
        ) : (
          step?.selfAdvances !== true && (
            <button
              type="button"
              disabled={!canLeave(step) || isLast}
              onClick={() => onGo(current + 1)}
              className="rounded-md border border-neutral-300 px-4 py-2 text-sm font-medium disabled:opacity-40 dark:border-neutral-700"
            >
              Next
            </button>
          )
        )}
        {/* ⚠ **The whole objection to a wizard, answered here.** A step you
            cannot leave says so, in the words of the rule that stopped you.
            **Skipped for a self-advancing step** — its `blockedBy`, if any,
            would only ever have explained why Next was stuck, and there is
            no Next here to explain. */}
        {step?.selfAdvances !== true && !canLeave(step) && step?.blockedBy && (
          <p className="text-sm text-neutral-600 dark:text-neutral-300">
            Still needed: {step.blockedBy}.
          </p>
        )}
      </div>
    </div>
  );
}
